/**
 * archivedTombstones — "did this browser already archive that thread?", read
 * from anywhere (2026-09-21, owner directive).
 *
 * WHY IT IS ITS OWN LEAF. The durable tombstone map is the cheapest true answer
 * in the whole closure ladder — a synchronous localStorage read, zero Firestore
 * cost, cross-tab, surviving a reload — and until today the only way to ask it
 * was `isThreadTombstonedInStorage`, exported from useChatThreads.ts: ~7,500
 * lines that import firebase and the entire chat. The bell needs the same
 * answer, and NotificationsBell + MobileNotifications mount on every marketing
 * page, so importing it there would drag the chat's whole module graph onto
 * pages that have no chat — and into every suite that renders one, which is the
 * `attachmentStorage` lesson in CLAUDE.md verbatim ("put the helper in its own
 * Firebase-free module instead of mocking firebase/auth in each suite").
 *
 * WHAT MOVED AND WHAT DID NOT. Only the READ side. `persistDurableTombstones`
 * stays in the hook because it writes through `quotaSafeSetItem`, and moving a
 * write path to get a read is how a behaviour changes by accident. The hook and
 * this module share ONE parser (`parseDurableTombstones`), so the two readers
 * cannot drift into disagreeing about what the stored map means.
 *
 * Pure and dependency-free: no firebase, no React, not even the chat's own
 * storage helpers — `readStoredTombstoneMap` below is byte-for-byte what
 * `getFromLocalStorage` does, inlined so this file imports nothing at all.
 */

/**
 * The map's key. UNCHANGED from the hook's own spelling — a second spelling
 * would silently read an empty map forever, which is the failure a shared
 * constant exists to make impossible.
 */
export function tombstoneStorageKey(instanceId: string, uid: string | null): string {
  const safeInstance = instanceId || 'global_chat';
  const safeUid = uid || 'anon';
  return `prest0_thread_archive_tombstones__${safeInstance}__u_${safeUid}`;
}

/**
 * The stored object, read as a live map with expired entries dropped.
 *
 * Tolerant in one direction only: anything malformed is absent rather than
 * thrown, because a corrupt storage value must never take down a render, and
 * "not tombstoned" is the safe answer (the thread renders open, which is what
 * it looked like before any of this existed).
 */
export function parseDurableTombstones(
  raw: Record<string, number> | null | undefined,
  now: number,
): Map<string, number> {
  const out = new Map<string, number>();
  if (!raw || typeof raw !== 'object') return out;
  for (const [threadId, expiresAt] of Object.entries(raw)) {
    if (!threadId) continue;
    if (typeof expiresAt !== 'number') continue;
    if (!Number.isFinite(expiresAt) || expiresAt <= now) continue;
    out.set(threadId, expiresAt);
  }
  return out;
}

/** The raw stored object. Identical to the chat's getFromLocalStorage, inlined. */
function readStoredTombstoneMap(key: string): Record<string, number> {
  try {
    if (typeof localStorage === 'undefined') return {};
    const stored = localStorage.getItem(key);
    if (stored === null) return {};
    return JSON.parse(stored) as Record<string, number>;
  } catch {
    return {};
  }
}

/** The live tombstone map for one surface + person. */
export function readDurableTombstones(
  instanceId: string,
  uid: string | null,
  now: number = Date.now(),
): Map<string, number> {
  return parseDurableTombstones(readStoredTombstoneMap(tombstoneStorageKey(instanceId, uid)), now);
}

/**
 * 2026-07-31 multi-device directive: read-only DURABLE-tombstone probe for
 * write paths that live OUTSIDE the thread hook (the recipient-side
 * shared-thread surfacing effect in GlobalChatSystem). Those paths used to call
 * upsertChatThread directly, bypassing writeThreadToServer's tombstone guard —
 * so a stub write could resurrect (or endlessly retry against) a thread the
 * user archived.
 *
 * Since 2026-09-21 it is also the closure ladder's free tier, for the mentions
 * tab and the bell alike. `useChatThreads` re-exports it under this exact name,
 * so every existing import site is untouched.
 */
export function isThreadTombstonedInStorage(
  instanceId: string,
  uid: string | null,
  threadId: string,
): boolean {
  if (!threadId) return false;
  return readDurableTombstones(instanceId, uid).has(threadId);
}
